// import { useMeta } from 'quasar';
import { log } from "@/lib/Debug";
import BGOMock from "./mockData/BGOMock";
import { useHead } from "@vueuse/head";

let BGO: BGOInstance
let isApp = false;
let isUseMock = false;

if (typeof window !== "undefined") {
  // 在客户端环境下执行的代码
  // 访问 window 对象以及相关逻辑
  BGO= window.BGO;
} else {
  // 在服务器端环境下执行的代码
  // 提供服务器端环境的替代逻辑
}

/**
 * 載入 BGO SDK
 */
export const loadSdk = async () => {
  log("load BGO SDK", import.meta.env.VITE_BGO_SDK);
  // useMeta({
  //   script: {
  //     beanfun: {
  //       src: import.meta.env.BGO_SDK,
  //       defer: '',
  //     },
  //   },
  // });
  useHead({
    script: [
      {
        src: import.meta.env.VITE_BGO_SDK,
        defer: "",
        onload: (e) => {
          console.log(e);
        },
      },
    ],
  });
  await checkSdkIsLoaded();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  BGO = window.BGO;
  isApp = await checkAppExist();
  isUseMock = !isApp && import.meta.env.VITE_NEED_IN_APP === "0";
  log("isApp:", isApp);
  log("isUseMock:", isUseMock);
};

/**
 * 等待並確認 SDK 載入
 */
export const checkSdkIsLoaded = () => {
  return new Promise((resolve) => {
    const timer = setInterval(() => {
      if (window.BGO) {
        log("BGO SDK: loaded!");
        clearInterval(timer);
        resolve(null);
      }
      log("BGO SDK: loading...");
    }, 10);
  });
};

/**
 * 初始化 BGO
 * @param  {String} officialAccountId 官方帳號序號
 * @param  {String} token 官方帳號憑證
 */
export const init = (officialAccountId: string, token: string) => {
  const officialAccountInfo = {
    official_account_id: officialAccountId,
    token: token,
  };
  const name = "BGO.init";
  log(`${name}(${JSON.stringify(officialAccountInfo)})`);
  BGO.init(officialAccountInfo, (data: { status: string }) => {
    log(data);
  });
};

/**
 * 判斷用戶是否存在於APP中
 * @return {promise<boolean>}
 */
export const checkAppExist = (): Promise<boolean> => {
  const name = "BGO.check_app_exist";
  log(`${name}()`);
  return new Promise((resolve) => {
    BGO.check_app_exist((res: { result: string }) => {
      log("checkAppExist", res);
      resolve(res.result === "ok");
    });
  });
};

/**
 * 取得是否在 APP 中，需再 loadSdk() 後使用
 * @return {Boolean}
 */
export const getIsApp = () => {
  return isApp;
};

/**
 * 取得使用者openid access_token and profile
 * @param  {String} clientId beanfun(樂豆) openid service 所發的 client id
 * @param  {String} [redirectUri=''] 請先填 "" (empty string)
 */
export const getOpenidAccessToken = (
  clientId: string,
  redirectUri = ""
): Promise<BGOOpenidAccessToken> => {
  const name = "BGO.get_me_openid_access_token";
  log(`${name}("${clientId}", "${redirectUri}")`);

  return new Promise((resolve, reject) => {
    if (isUseMock) {
      const mockData = BGOMock.openidAccessToken;
      log("getOpenidAccessToken:", "[MOCK]", mockData);
      resolve(mockData);
      return;
    }
    BGO.get_me_openid_access_token(
      clientId,
      redirectUri,
      (data: BGOOpenidAccessToken & BGOError) => {
        log("getOpenidAccessToken:", data);
        if (data.error) {
          reject();
          return;
        }
        resolve(data);
      }
    );
  });
};

/** 取得使用者資訊 */
export const getMeProfile = (): Promise<BGOMeProfile> => {
  const name = "BGO.get_me_profile";
  log(`${name}()`);

  return new Promise((resolve) => {
    if (isUseMock) {
      log("getMeProfile:", "[MOCK]", BGOMock.profile);
      resolve(BGOMock.profile);
      return;
    }
    BGO.get_me_profile((data: BGOMeProfile) => {
      log("getMeProfile:", data);
      resolve(data);
    });
  });
};

/** 取得 open id */
export const getEncryptedOpenId = (): Promise<string> => {
  const name = "BGO.get_encrypted_open_id";
  log(`${name}()`);

  return new Promise((resolve) => {
    if (isUseMock) {
      log("getEncryptedOpenId:", "[MOCK]", BGOMock.openId);
      resolve(BGOMock.openId);
      return;
    }
    try {
      const errorTimer = setTimeout(() => {
        resolve("");
      }, 1000);
      BGO.get_encrypted_open_id((data: string) => {
        clearTimeout(errorTimer);
        log("getEncryptedOpenId:", data);
        resolve(data);
      });
    } catch (e) {
      resolve("");
    }
  });
};

/** 取得 tracking 需要的參數 */
export const getTrackingSessionData = (): Promise<BGOTrackingSessionData> => {
  const name = "BGO.get_tracking_session_data";
  log(`${name}()`);

  return new Promise((resolve) => {
    if (isUseMock) {
      log("getTrackingSessionData:", "[MOCK]", BGOMock.trackingSessionData);
      resolve(BGOMock.trackingSessionData);
      return;
    }
    BGO.get_tracking_session_data((data: BGOTrackingSessionData) => {
      log("getTrackingSessionData:", data);
      resolve(data);
    });
  });
};

/**
 * @param  {String} link 開啟的連結
 * @param  {String} title='漫畫星'
 * @return {Promise}
 */
export const openFullH5Webview = (link: string, title = "漫畫星") => {
  const officialAccountId = import.meta.env.VITE_BGO_OFFICIAL_ACCOUNT_ID;
  const name = "BGO.open_full_h5_webview";
  log(`${name}("${link}", "${title}", "${officialAccountId}")`);
  return new Promise((resolve, reject) => {
    if (!isApp) {
      window.open(link);
      resolve(null);
      return;
    }
    BGO.open_full_h5_webview(
      link,
      title,
      officialAccountId,
      (data: BGOError) => {
        if (data.error) {
          log("openFullH5Webview:", data);
          reject();
          return;
        }
        resolve(null);
      }
    );
  });
};

export const deeplinkJump = (deeplink: string, leaveWebview = false) => {
  const name = "BGO.deeplink_jump";
  log(`${name}("${deeplink}", "${leaveWebview.toString()}")`);
  return new Promise((resolve) => {
    if (!isApp) {
      window.open(deeplink);
      resolve(null);
      return;
    }
    BGO.deeplink_jump(deeplink, leaveWebview);
    resolve(null);
  });
};
/**
 *
 * @param messageBody
 * @param selectOpt
 * @returns
 */
export const sendMessageV2 = (
  messageBody: BGOMessageBody,
  selectOpt: BGOMessageSelectOpt = {
    title: "轉傳至",
    target: {
      friend: 1,
      group: 1,
      fun_chat: 1,
      game: 1,
    },
    max_selected_count: -1,
  }
) => {
  const name = "BGO.send_message_v2";
  log(name, "(", messageBody, selectOpt, ")");

  return new Promise((resolve, reject) => {
    BGO.send_message_v2(messageBody, selectOpt, (data: BGOError) => {
      log("sendMessageV2:", data);
      if (data.error) {
        reject();
        return;
      }
      resolve(data);
    });
  });
};

/**
 * 建立要傳送的訊息(App Link)
 */
export const createMessageAppLink = ({
  description,
  image,
  link,
  title,
}: {
  description: string;
  image: string;
  link: string;
  title: string;
}): BGOMessageBody => {
  return {
    messages: [
      {
        raw_message: {
          mime_type: 81,
          content: description,
          metadata: {
            application_x_beanfun_interactive_metadata: {
              service_id: import.meta.env.VITE_BGO_WIDGET_ID,
              image_info: {
                mime_type: 10,
                uri: image,
                thumbnail_uri: image,
              },
              once: false,
              forwardable: true,
              expired_hint: "新聞過期",
              options: [
                {
                  opt_key: "app_link_option_key",
                  opt_type: 2,
                  description: title,
                  uri: link,
                },
              ],
              interactive_key: "sdk_interactive_key",
            },
          },
          expired_time: -1,
        },
      },
    ],
  };
};

/**
 * 分享訊息到其它 APP
 */
export const sendDataToApps = (text: string) => {
  const name = "BGO.send_data_to_apps";
  log(`${name}(${JSON.stringify({ text })})`);
  BGO.send_data_to_apps({ text });
};

/**
 * 取得螢幕亮度
 */
export const getBrightness = (): Promise<number> => {
  const name = "BGO.get_brightness";
  log(`${name}()`);
  return new Promise((resolve) => {
    BGO.get_brightness((data: BGOError & { brightness: number }) => {
      log("getBrightness:", data);
      if (data.error) {
        resolve(20);
        return;
      }
      resolve(data.brightness);
    });
  });
};

/**
 * 設定螢幕亮度
 */
export const setBrightness = (brightness: number) => {
  const name = "BGO.set_brightness";
  log(`${name}(${brightness})`);
  BGO.set_brightness(brightness);
};

/**
 * 前往儲值頁
 */
export const goBfcoinPurchase = () => {
  const name = "BGO.go_bfcoin_purchase";
  log(`${name}`);
  BGO.go_bfcoin_purchase();
};

/**
 * BGO分享清單功能
 */
export const shareWithSharesheet = (
  text: string,
  link: string,
  source: string
) => {
  const name = "BGO.share_with_sharesheet";
  log(`${name}`);
  BGO.share_with_sharesheet(text, link, source);
};

/**
 * 關閉擴充功能
 */
export const leaveExtension = () => {
  const name = "BGO.leave_extension:";
  log(`${name}`);
  BGO.leave_extension();
};

/**
 * 開啟預設瀏覽器
 */
export const openDefaultBrowser = (url: string) => {
  const name = "BGO.redirect_uri_by_default_browser";
  log(`${name}`);
  BGO.redirect_uri_by_default_browser(url);
};

export default {
  loadSdk,
  checkSdkIsLoaded,
  init,
  checkAppExist,
  getIsApp,
  getOpenidAccessToken,
  getMeProfile,
  getEncryptedOpenId,
  getTrackingSessionData,
  openFullH5Webview,
  deeplinkJump,
  sendMessageV2,
  createMessageAppLink,
  sendDataToApps,
  getBrightness,
  setBrightness,
  goBfcoinPurchase,
  shareWithSharesheet,
  leaveExtension,
  openDefaultBrowser,
};
